<script setup>
import { ref, onMounted, computed, nextTick } from "vue";
import JsBarcode from "jsbarcode";
import { isTauri } from "@tauri-apps/api/core";
import Swal from "sweetalert2";
import ExcelJS from "exceljs";
import {
  listInventory,
  updateProductBasic,
  softDeleteProduct,
} from "../db/inventory.js";
import { currentUser } from "../db/auth.js";
import InventoryModal from "../components/InventoryModal.vue";
import ChatAssistant from "../components/ChatAssistant.vue";

const items = ref([]);
const filtered = ref([]);
const search = ref("");
const showSuggestions = ref(false);
const hoverIndex = ref(-1);
const loading = ref(false);
const error = ref("");
const selectedId = ref(null);
const showCreate = ref(false);
const showEdit = ref(false);
const editProduct = ref(null);

const page = ref(1);
const pageSize = 10;
const totalPages = computed(() => {
  const len = filtered.value.length;
  return Math.max(1, Math.ceil(len / pageSize));
});
const pageItems = computed(() => {
  const start = (page.value - 1) * pageSize;
  return filtered.value.slice(start, start + pageSize);
});

// Verificar si el usuario es admin o si está en Tauri
const canModify = computed(() => {
  const user = currentUser();
  if (!user) return isTauri(); // Sin sesión, solo en Tauri
  const role = String(user.role || "").toLowerCase();
  const isAdmin = ["admin", "superadmin"].includes(role) || user?.role_id === 1;
  // Admin puede modificar siempre, otros usuarios solo en Tauri
  return isAdmin || isTauri();
});

// UI demo chips (comida, se aproximan al mockup)
const demoVariants = ["Al pastor", "Asada", "Barbacoa", "Veggie"];
const demoPortions = ["Individual", "Doble", "Familiar"];

const selectedItem = computed(
  () => items.value.find((r) => r.id === selectedId.value) || null
);

// Estadísticas computadas
const totalProducts = computed(() => items.value.length);
const activeProducts = computed(() => items.value.filter(p => Number(p.stock_qty || 0) > 0).length);
const lowStockProducts = computed(() => 
  items.value.filter(p => 
    Number(p.reorder_level || 0) > 0 && 
    Number(p.stock_qty || 0) <= Number(p.reorder_level || 0)
  ).length
);
const totalValue = computed(() => {
  return items.value.reduce((sum, p) => {
    const qty = Number(p.stock_qty || 0);
    const price = Number(p.unit_price || 0);
    return sum + (qty * price);
  }, 0);
});

onMounted(load);

async function load() {
  loading.value = true;
  try {
    const data = await listInventory();
    items.value = data;
    applyFilter();
    await nextTick();
    renderBarcodes();
  } catch (e) {
    error.value = String(e);
  } finally {
    loading.value = false;
  }
}

function applyFilter() {
  const q = search.value.trim().toLowerCase();
  filtered.value = !q
    ? items.value
    : items.value.filter(
        (i) =>
          (i.name || "").toLowerCase().includes(q) ||
          (i.sku || "").toLowerCase().includes(q)
      );
  page.value = 1;
  nextTick(renderBarcodes);
}

const suggestions = computed(() => {
  const q = search.value.trim().toLowerCase();
  const base = !q
    ? items.value.slice()
    : items.value.filter(
        (i) =>
          (i.name || "").toLowerCase().includes(q) ||
          (i.sku || "").toLowerCase().includes(q)
      );
  return base.slice(0, 8);
});

function selectRow(id) {
  selectedId.value = id;
}

async function removeSelected() {
  if (!selectedId.value) return;
  try {
    await softDeleteProduct(selectedId.value);
    await load();
    selectedId.value = null;
  } catch (e) {
    error.value = String(e);
  }
}

function modifySelected() {
  const row = items.value.find((r) => r.id === selectedId.value);
  if (!row) return;
  if (!canModify.value) return; // Deshabilitado sin permisos
  editProduct.value = { ...row };
  showEdit.value = true;
}

async function deleteRow(id) {
  selectedId.value = id;
  const result = await Swal.fire({
    title: "¿Eliminar producto?",
    text: "Esta acción moverá el producto a eliminado.",
    icon: "warning",
    showCancelButton: true,
    confirmButtonText: "Eliminar",
    cancelButtonText: "Cancelar",
    reverseButtons: true,
    confirmButtonColor: "#ef4444",
  });
  if (!result.isConfirmed) return;
  if (!canModify.value) {
    // Sin permisos
    await Swal.fire({
      title: "Acción no permitida",
      text: "No tienes permisos para eliminar productos.",
      icon: "info",
    });
    return;
  }
  await removeSelected();
  await Swal.fire({
    title: "Eliminado",
    text: "El producto ha sido eliminado.",
    icon: "success",
    timer: 1500,
    showConfirmButton: false,
  });
}

function editRow(id) {
  selectedId.value = id;
  if (!canModify.value) return; // Deshabilitado sin permisos
  modifySelected();
}

function onSearchFocus() {
  showSuggestions.value = true;
}
function onSearchBlur() {
  setTimeout(() => (showSuggestions.value = false), 180);
}
function onSearchInput() {
  applyFilter();
  showSuggestions.value = true;
}
function onSearchKeydown(e) {
  if (!showSuggestions.value || !suggestions.value.length) return;
  if (e.key === "ArrowDown") {
    e.preventDefault();
    hoverIndex.value = (hoverIndex.value + 1) % suggestions.value.length;
  } else if (e.key === "ArrowUp") {
    e.preventDefault();
    hoverIndex.value = (hoverIndex.value - 1 + suggestions.value.length) % suggestions.value.length;
  } else if (e.key === "Enter") {
    e.preventDefault();
    const row = suggestions.value[hoverIndex.value >= 0 ? hoverIndex.value : 0];
    if (row) selectSuggestion(row);
  } else if (e.key === "Escape") {
    showSuggestions.value = false;
  }
}
function selectSuggestion(row) {
  search.value = row.name || row.sku || "";
  applyFilter();
  selectRow(row.id);
  showSuggestions.value = false;
}

function renderBarcodes() {
  try {
    for (const r of pageItems.value) {
      const el = document.getElementById(`bc-${r.id}`);
      if (!el) continue;
      if (!r.barcode) {
        el.innerHTML = "";
        continue;
      }
      try {
        JsBarcode(el, String(r.barcode), {
          format: "CODE128",
          displayValue: true,
          lineColor: "#111827",
          fontSize: 12,
          height: 36,
          margin: 4,
          textAlign: "center",
        });
      } catch {
        el.innerHTML = "";
      }
    }
  } catch {}
}

function gotoPage(p) {
  if (p < 1) p = 1;
  if (p > totalPages.value) p = totalPages.value;
  page.value = p;
  nextTick(renderBarcodes);
}
function prevPage() { gotoPage(page.value - 1); }
function nextPage() { gotoPage(page.value + 1); }

function onSaved() {
  showCreate.value = false;
  load();
}
async function onSavedEdit() {
  showEdit.value = false;
  await Swal.fire({
    title: "Actualizado",
    text: "El producto ha sido actualizado.",
    icon: "success",
    timer: 1500,
    showConfirmButton: false,
  });
  load();
}

async function exportExcel() {
  try {
    if (!items.value.length) {
      await Swal.fire({
        title: "Sin datos",
        text: "No hay productos para exportar.",
        icon: "info",
      });
      return;
    }
    const wb = new ExcelJS.Workbook();
    const ws = wb.addWorksheet("Inventario");
    ws.columns = [
      { header: "SKU", key: "sku" },
      { header: "Código de barras", key: "barcode" },
      { header: "Nombre", key: "name" },
      { header: "Categoria", key: "category" },
      { header: "Proveedor", key: "supplier" },
      { header: "Costo", key: "cost" },
      { header: "Precio base", key: "unit" },
      { header: "IVA (%)", key: "tax" },
      { header: "Descuento (%)", key: "discount" },
      { header: "Precio final", key: "final" },
      { header: "Stock", key: "stock" },
      { header: "Reorden", key: "reorder" },
      { header: "Descripción", key: "desc" },
      { header: "Notas", key: "notes" },
      { header: "Creado", key: "created" },
    ];
    const headerStyle = {
      fill: { type: "pattern", pattern: "solid", fgColor: { argb: "C49A6C" } },
      font: { color: { argb: "FFFFFFFF" }, bold: true },
      alignment: { horizontal: "center" },
      border: {
        top: { style: "thin", color: { argb: "A57C52" } },
        bottom: { style: "thin", color: { argb: "A57C52" } },
        left: { style: "thin", color: { argb: "A57C52" } },
        right: { style: "thin", color: { argb: "A57C52" } },
      },
    };
    ws.getRow(1).eachCell((cell) => { cell.style = headerStyle; });
    for (const r of items.value) {
      ws.addRow({
        sku: r.sku || "N/A",
        barcode: r.barcode || "N/A",
        name: r.name || "N/A",
        category: r.category || "N/A",
        supplier: r.supplier || "N/A",
        cost: Number(r.cost_price || 0),
        unit: Number(r.unit_price || 0),
        tax: (r.tax_rate != null ? Number(r.tax_rate) : 0) / 100,
        discount: Number(r.discount || 0) / 100,
        final: Number(priceFinal(r.unit_price, r.discount, r.tax_rate)),
        stock: Number(r.stock_qty || 0),
        reorder: Number(r.reorder_level || 0),
        desc: r.description || "N/A",
        notes: r.notes || "N/A",
        created: r.created_at ? new Date(r.created_at) : "N/A",
      });
    }
    ws.getColumn(6).numFmt = "$#,##0.00";       // Costo
    ws.getColumn(7).numFmt = "$#,##0.00";       // Precio base
    ws.getColumn(8).numFmt = "0%";             // IVA
    ws.getColumn(9).numFmt = "0%";             // Descuento
    ws.getColumn(10).numFmt = "$#,##0.00";      // Precio final
    ws.getColumn(11).numFmt = "0";             // Stock
    ws.getColumn(12).numFmt = "0";             // Reorden
    ws.getColumn(15).numFmt = "dd/mm/yyyy";    // Creado

    ws.columns.forEach((col) => {
      let max = (col.header ? String(col.header).length : 10);
      col.eachCell({ includeEmpty: true }, (cell) => {
        let v = cell.value;
        if (v == null) return;
        let len = 0;
        if (v instanceof Date) {
          len = 10; // dd/mm/yyyy
        } else if (typeof v === "object" && v.text) {
          len = String(v.text).length;
        } else {
          len = String(v).length;
        }
        if (len > max) max = len;
      });
      col.width = Math.min(Math.max(max + 2, 10), 40);
    });

    ws.eachRow((row) => {
      row.eachCell((cell) => {
        cell.alignment = { horizontal: "center", vertical: "middle" };
        cell.border = {
          top: { style: "thin", color: { argb: "A57C52" } },
          bottom: { style: "thin", color: { argb: "A57C52" } },
          left: { style: "thin", color: { argb: "A57C52" } },
          right: { style: "thin", color: { argb: "A57C52" } },
        };
      });
    });
    const arrayBuf = await wb.xlsx.writeBuffer();
    const blob = new Blob([arrayBuf], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    const now = new Date();
    const yyyy = now.getFullYear();
    const mm = String(now.getMonth() + 1).padStart(2, "0");
    const dd = String(now.getDate()).padStart(2, "0");
    a.download = `inventario_${yyyy}-${mm}-${dd}.xlsx`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
    await Swal.fire({
      title: "Exportado",
      text: "Se exportó el inventario a Excel.",
      icon: "success",
      timer: 1500,
      showConfirmButton: false,
    });
  } catch (e) {
    await Swal.fire({
      title: "Error al exportar",
      text: String(e),
      icon: "error",
    });
  }
}

function priceFinal(price, discount, taxRate) {
  const p = Number(price || 0);
  const d = Math.min(Math.max(Number(discount || 0), 0), 100);
  const after = p * (1 - d / 100);
  const tr = taxRate != null ? Number(taxRate) : null;
  return after * (tr != null ? 1 + tr / 100 : 1);
}

function formatDMY(value) {
  if (!value) return "";
  const d = new Date(value);
  if (isNaN(d.getTime())) return "";
  const dd = String(d.getDate()).padStart(2, "0");
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const yy = d.getFullYear();
  return `${dd}/${mm}/${yy}`;
}
</script>

<template>
  <div class="layout">
    <section class="panel-left">
      <!-- Header con título -->
      <div class="page-header">
        <h1 class="page-title-main">Inventario</h1>
        <p class="page-subtitle">Gestión de productos y stock</p>
      </div>

      <!-- Tarjetas de estadísticas -->
      <div class="stats-grid">
        <div class="stat-card stat-orange">
          <div class="stat-icon">
            <span class="material-symbols-rounded">inventory_2</span>
          </div>
          <div class="stat-content">
            <div class="stat-label">TOTAL PRODUCTOS</div>
            <div class="stat-value">{{ totalProducts }}</div>
          </div>
        </div>
        
        <div class="stat-card stat-blue">
          <div class="stat-icon">
            <span class="material-symbols-rounded">check_circle</span>
          </div>
          <div class="stat-content">
            <div class="stat-label">PRODUCTOS ACTIVOS</div>
            <div class="stat-value">{{ activeProducts }}</div>
          </div>
        </div>
        
        <div class="stat-card stat-green">
          <div class="stat-icon">
            <span class="material-symbols-rounded">warning</span>
          </div>
          <div class="stat-content">
            <div class="stat-label">STOCK BAJO</div>
            <div class="stat-value">{{ lowStockProducts }}</div>
          </div>
        </div>
        
        <div class="stat-card stat-purple">
          <div class="stat-icon">
            <span class="material-symbols-rounded">attach_money</span>
          </div>
          <div class="stat-content">
            <div class="stat-label">VALOR TOTAL</div>
            <div class="stat-value">${{ totalValue.toLocaleString('es-MX', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) }}</div>
          </div>
        </div>
      </div>

      <!-- Barra de herramientas y filtros -->
      <div class="toolbar">
        <div class="toolbar-search">
          <div class="input-wrap">
            <span class="material-symbols-rounded input-icon" aria-hidden="true"
              >search</span
            >
            <input
              class="search"
              v-model="search"
              @input="onSearchInput"
              @focus="onSearchFocus"
              @blur="onSearchBlur"
              @keydown="onSearchKeydown"
              placeholder="Buscar por nombre o SKU"
            />
            <button
              v-if="search"
              class="clear-btn"
              @click="search=''; applyFilter(); showSuggestions=false"
              aria-label="Limpiar búsqueda"
            >
              <span class="material-symbols-rounded" aria-hidden="true">close</span>
            </button>
            <div class="suggestions" v-if="showSuggestions && suggestions.length">
              <button
                v-for="(s, idx) in suggestions"
                :key="s.id"
                class="suggestion-item"
                :class="{ active: idx === hoverIndex }"
                type="button"
                @mouseenter="hoverIndex = idx"
                @mousedown.prevent="selectSuggestion(s)"
              >
                <img
                  v-if="s.image_base64"
                  class="suggestion-thumb"
                  :src="`data:${s.image_mime || 'image/jpeg'};base64,${s.image_base64}`"
                  alt=""
                />
                <span class="suggestion-name">{{ s.name }}</span>
                <span class="suggestion-sku">{{ s.sku || 'N/A' }}</span>
                <span class="suggestion-price">${{ Number(priceFinal(s.unit_price, s.discount, s.tax_rate)).toFixed(2) }}</span>
              </button>
            </div>
          </div>
        </div>
        
        <div class="toolbar-actions">
          <button class="btn-secondary" title="Exportar a Excel" @click="exportExcel">
            <span class="material-symbols-rounded" aria-hidden="true">download</span>
            <span>Exportar a Excel</span>
          </button>
          
          <button
            class="btn-primary"
            title="Nuevo Producto"
            :disabled="!canModify"
            @click="showCreate = true"
          >
            <span class="material-symbols-rounded" aria-hidden="true">add</span>
            <span>Nuevo Producto</span>
          </button>
        </div>
      </div>

      <p v-if="error" class="error">{{ error }}</p>

      <div class="card">
        <div class="table-scroll">
        <table class="list">
          <thead>
            <tr>
              <th>Imagen</th>
              <th>Nombre</th>
              <th>Compañía</th>
              <th>SKU</th>
              <th>Código de barras</th>
              <th>Reorden</th>
              <th>Existencia</th>
              <th>Precio</th>
              <th class="actions-col">Acciones</th>
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="row in pageItems"
              :key="row.id"
              :class="{ selected: selectedId === row.id }"
              @click="selectRow(row.id)"
            >
              <td class="thumb-col">
                <img
                  v-if="row.image_base64"
                  class="thumb"
                  :src="`data:${row.image_mime || 'image/jpeg'};base64,${
                    row.image_base64
                  }`"
                  alt=""
                />
                <div v-else class="thumb placeholder">📦</div>
              </td>
              <td class="name">{{ row.name }}</td>
              <td class="company">{{ row.supplier || "N/A" }}</td>
              <td>{{ row.sku || "N/A" }}</td>
              <td>
                <canvas :id="`bc-${row.id}`" class="barcode-mini"></canvas>
              </td>
              <td class="reorder">
                {{ Number(row.reorder_level || 0).toFixed(0) }}
                <span
                  v-if="Number(row.reorder_level || 0) > 0 && Number(row.stock_qty || 0) <= Number(row.reorder_level || 0)"
                  class="badge warn"
                  title="Stock por debajo del nivel de reorden"
                >Poco stock</span>
              </td>
              <td class="qty">{{ Number(row.stock_qty).toFixed(0) }}</td>
              <td class="price">${{ Number(priceFinal(row.unit_price, row.discount, row.tax_rate)).toFixed(2) }}</td>
              <td>
                <div class="row-actions" @click.stop>
                  <button
                    class="act-btn"
                    :disabled="!canModify"
                    @click="editRow(row.id)"
                  >
                    <span class="material-symbols-rounded" aria-hidden="true">edit</span>
                    
                  </button>
                  <button
                    class="act-btn warn"
                    :disabled="!canModify"
                    @click="deleteRow(row.id)"
                  >
                    <span class="material-symbols-rounded" aria-hidden="true">delete</span>
                    
                  </button>
                </div>
              </td>
            </tr>
            <tr v-if="!filtered.length">
              <td colspan="9" class="empty">
                Sin registros.
                {{
                  canModify
                    ? "Captura un producto con el botón \"Nuevo producto\"."
                    : "No tienes permisos para agregar productos."
                }}
              </td>
            </tr>
          </tbody>
        </table>
        </div>
      </div>
      <div class="pagination" v-if="totalPages > 1">
        <button class="pager-btn" :disabled="page <= 1" @click="prevPage">
          <span class="material-symbols-rounded" aria-hidden="true">chevron_left</span>
        </button>
        <span class="pager-info">Página {{ page }} de {{ totalPages }}</span>
        <button class="pager-btn" :disabled="page >= totalPages" @click="nextPage">
          <span class="material-symbols-rounded" aria-hidden="true">chevron_right</span>
        </button>
      </div>
    </section>

    <aside class="panel-right">
      <div class="card detail" v-if="selectedItem">
        <h3 class="detail-title">Detalles del producto</h3>

        <div class="detail-image" v-if="selectedItem.image_base64">
          <img
            :src="`data:${selectedItem.image_mime || 'image/jpeg'};base64,${
              selectedItem.image_base64
            }`"
            alt="Imagen del producto"
          />
        </div>
        <div class="thumbs" v-if="selectedItem.image_base64">
          <img
            v-for="n in 4"
            :key="n"
            class="thumb-mini"
            :src="`data:${selectedItem.image_mime || 'image/jpeg'};base64,${
              selectedItem.image_base64
            }`"
            alt="Miniatura"
          />
        </div>

        <div class="chips">
          <span class="chip">SKU: {{ selectedItem.sku || "-" }}</span>
          <span class="chip success"
            >Stock: {{ Number(selectedItem.stock_qty || 0).toFixed(0) }}</span
          >
          <span class="chip price"
            >${{ Number(priceFinal(selectedItem.unit_price, selectedItem.discount, selectedItem.tax_rate)).toFixed(2) }}</span
          >
        </div>

        <div class="section">
          <div class="section-head">Variantes</div>
          <div class="chip-row">
            <button v-for="v in demoVariants" :key="v" class="chip-btn">
              {{ v }}
            </button>
          </div>
        </div>

        <div class="section">
          <div class="section-head">
            Tamaño de porción
            <span class="muted small"
              >• Sólo {{ Number(selectedItem.stock_qty || 0) }} en stock</span
            >
          </div>
          <div class="chip-row">
            <button v-for="p in demoPortions" :key="p" class="chip-btn">
              {{ p }}
            </button>
          </div>
        </div>

        <div class="section">
          <div class="section-head">Descripción</div>
          <ul class="desc-list">
            <li>Tacos preparados al momento con tortilla recién hecha.</li>
            <li>Ingredientes frescos y salsas artesanales.</li>
            <li>Carne marinada y acompañada con cebolla y cilantro.</li>
          </ul>
        </div>

        <div class="section">
          <div class="section-head">Preparación y entrega</div>
          <div class="shipping-grid">
            <div class="ship-item">
              <span class="material-symbols-rounded">restaurant</span>
              <b>Preparado en cocina</b>
            </div>
            <div class="ship-item">
              <span class="material-symbols-rounded">delivery_dining</span>
              <b>Entrega local</b>
            </div>
            <div class="ship-item">
              <span class="material-symbols-rounded">location_on</span>
              <b>Origen: {{ selectedItem.supplier || "Restaurante" }}</b>
            </div>
            <div class="ship-item">
              <span class="material-symbols-rounded">schedule</span>
              <b>Tiempo estimado: 20–30 min</b>
            </div>
          </div>
        </div>

        <div class="section">
          <div class="section-head">Reviews</div>
          <div class="reviews">
            <div class="review-card">
              <div class="review-head">
                <b>Alex Mathio</b> <span class="stars">★★★★★</span>
              </div>
              <p class="muted">
                Excelente calidad y prácticas sostenibles. Recomendado por su
                diseño y confort.
              </p>
            </div>
            <div class="review-card">
              <div class="review-head">
                <b>Alex Mathio</b> <span class="stars">★★★★★</span>
              </div>
              <p class="muted">
                Entrega rápida y atención notable. Elegante y versátil para el
                día a día.
              </p>
            </div>
          </div>
        </div>

        <div class="detail-grid">
          <div class="detail-row">
            <span>Precio</span
            ><b>${{ Number(priceFinal(selectedItem.unit_price, selectedItem.discount, selectedItem.tax_rate)).toFixed(2) }}</b>
          </div>
          <div class="detail-row">
            <span>Existencia</span
            ><b>{{ Number(selectedItem.stock_qty || 0).toFixed(0) }}</b>
          </div>
          <div class="detail-row">
            <span>Proveedor</span><b>{{ selectedItem.supplier || "N/A" }}</b>
          </div>
          <div class="detail-row">
            <span>Categoría</span><b>{{ selectedItem.category || "N/A" }}</b>
          </div>
        </div>
      </div>
      <div class="card detail" v-else>
        <h3 class="detail-title">Selecciona una fila</h3>
        <p class="muted">Verás aquí el resumen del elemento.</p>
      </div>
    </aside>

    <InventoryModal
      :open="showCreate"
      @close="showCreate = false"
      @saved="onSaved"
    />
    <InventoryModal
      :open="showEdit"
      :product="editProduct"
      @close="showEdit = false"
      @saved="onSavedEdit"
    />
    <ChatAssistant />
  </div>
</template>

<style scoped>
.layout {
  display: grid;
  grid-template-columns: 1.6fr 1fr;
  gap: 1.5rem;
  padding: 1.5rem;
  max-width: 1920px;
  margin: 0 auto;
  background: linear-gradient(135deg, #0a0b14 0%, #1a1610 100%);
  min-height: 100vh;
}

.panel-left,
.panel-right {
  display: grid;
  align-content: start;
  gap: 1.25rem;
}

/* Header */
.page-header {
  margin-bottom: 0.5rem;
}

.page-title-main {
  font-size: 2rem;
  font-weight: 700;
  color: var(--text);
  margin: 0 0 0.25rem 0;
  letter-spacing: -0.5px;
}

.page-subtitle {
  font-size: 1rem;
  color: var(--muted);
  margin: 0;
}

/* Tarjetas de estadísticas */
.stats-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(260px, 1fr));
  gap: 1.25rem;
  margin-bottom: 0.5rem;
}

.stat-card {
  display: flex;
  align-items: center;
  gap: 1.25rem;
  padding: 1.75rem 1.5rem;
  border-radius: 20px;
  position: relative;
  overflow: hidden;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  cursor: default;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
}

.stat-card::before {
  content: '';
  position: absolute;
  top: 0;
  right: 0;
  width: 140px;
  height: 140px;
  border-radius: 50%;
  opacity: 0.15;
  transition: transform 0.3s ease;
}

.stat-card:hover {
  transform: translateY(-4px);
  box-shadow: 0 12px 32px rgba(0, 0, 0, 0.12);
}

.stat-card:hover::before {
  transform: scale(1.2);
}

.stat-orange {
  background: linear-gradient(135deg, #FF9A3D 0%, #FF6B3D 100%);
}

.stat-orange::before {
  background: #fff;
  top: -40px;
  right: -40px;
}

.stat-blue {
  background: linear-gradient(135deg, #C6923D 0%, #DFC05E 100%);
}

.stat-blue::before {
  background: #fff;
  top: -40px;
  right: -40px;
}

.stat-green {
  background: linear-gradient(135deg, #10b981 0%, #059669 100%);
}

.stat-green::before {
  background: #fff;
  top: -40px;
  right: -40px;
}

.stat-purple {
  background: linear-gradient(135deg, #C6923D 0%, #C6923D 100%);
}

.stat-purple::before {
  background: #fff;
  top: -40px;
  right: -40px;
}

.stat-icon {
  width: 64px;
  height: 64px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(255, 255, 255, 0.25);
  border-radius: 16px;
  backdrop-filter: blur(10px);
  flex-shrink: 0;
}

.stat-icon .material-symbols-rounded {
  font-size: 32px;
  color: #ffffff;
  font-variation-settings: 'FILL' 0, 'wght' 400, 'GRAD' 0, 'opsz' 48;
}

.stat-content {
  flex: 1;
  color: #ffffff;
}

.stat-label {
  font-size: 0.8rem;
  font-weight: 600;
  letter-spacing: 0.5px;
  opacity: 0.95;
  margin-bottom: 0.5rem;
}

.stat-value {
  font-size: 2rem;
  font-weight: 700;
  line-height: 1;
  letter-spacing: -0.5px;
}

/* Toolbar */
.toolbar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  background: var(--surface);
  border: 1px solid var(--border);
  border-radius: 20px;
  padding: 1rem 1.25rem;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.04);
  backdrop-filter: blur(8px);
  position: relative;
  z-index: 100;
}

.toolbar-search {
  flex: 0 1 auto;
  max-width: 400px;
  min-width: 280px;
}

.toolbar-actions {
  display: flex;
  gap: 0.75rem;
  align-items: center;
}
/* Search Input */
.input-wrap {
  position: relative;
  width: 100%;
  z-index: 100;
}

.input-icon {
  position: absolute;
  left: 16px;
  top: 50%;
  transform: translateY(-50%);
  color: var(--muted);
  font-size: 22px;
  pointer-events: none;
  z-index: 1;
}

.search {
  width: 100%;
  padding: 0.875rem 3rem 0.875rem 3.25rem;
  border-radius: 14px;
  border: 2px solid transparent;
  background: rgba(223, 192, 94, 0.04);
  color: var(--text);
  font-size: 0.95rem;
  font-weight: 500;
  transition: all 0.25s cubic-bezier(0.4, 0, 0.2, 1);
}

.search::placeholder {
  color: var(--muted);
  font-weight: 400;
}

.search:hover {
  background: rgba(223, 192, 94, 0.06);
}

.search:focus {
  outline: none;
  border-color: #667eea;
  background: var(--surface);
  box-shadow: 0 0 0 4px rgba(102, 126, 234, 0.1);
}

.clear-btn {
  position: absolute;
  right: 12px;
  top: 50%;
  transform: translateY(-50%);
  width: 32px;
  height: 32px;
  border-radius: 10px;
  border: none;
  background: rgba(223, 192, 94, 0.08);
  color: var(--muted);
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.2s ease;
  z-index: 1;
}

.clear-btn:hover {
  background: rgba(223, 192, 94, 0.15);
  color: var(--accent);
  transform: translateY(-50%) scale(1.05);
}
/* Suggestions Dropdown */
.suggestions {
  position: absolute;
  left: 0;
  top: calc(100% + 10px);
  width: 100%;
  background: #1a1d29;
  border: 1px solid rgba(198, 146, 61, 0.3);
  border-radius: 16px;
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.5);
  z-index: 9999;
  overflow: hidden;
  backdrop-filter: blur(20px);
  animation: slideDown 0.2s ease;
}

@media (prefers-color-scheme: light) {
  .suggestions {
    background: #ffffff;
    border-color: var(--border);
    box-shadow: 0 20px 40px rgba(0, 0, 0, 0.15);
  }
}

@keyframes slideDown {
  from {
    opacity: 0;
    transform: translateY(-10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.suggestion-item {
  display: grid;
  grid-template-columns: 42px 1fr auto auto;
  gap: 0.875rem;
  align-items: center;
  padding: 0.875rem 1rem;
  width: 100%;
  background: transparent;
  border: none;
  cursor: pointer;
  color: #e5e7eb;
  border-bottom: 1px solid rgba(255, 255, 255, 0.08);
  transition: all 0.2s ease;
  text-align: left;
}

.suggestion-item:last-child {
  border-bottom: none;
}

.suggestion-item:hover,
.suggestion-item.active {
  background: linear-gradient(90deg, rgba(198, 146, 61, 0.15) 0%, rgba(124, 58, 237, 0.1) 100%);
  padding-left: 1.25rem;
}

@media (prefers-color-scheme: light) {
  .suggestion-item {
    color: var(--text);
    border-bottom-color: var(--border);
  }
  
  .suggestion-item:hover,
  .suggestion-item.active {
    background: linear-gradient(90deg, rgba(223, 192, 94, 0.08) 0%, rgba(102, 126, 234, 0.05) 100%);
  }
}

.suggestion-thumb {
  width: 42px;
  height: 42px;
  object-fit: cover;
  object-position: center;
  border-radius: 10px;
  border: 2px solid rgba(198, 146, 61, 0.3);
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.3);
  flex-shrink: 0;
  display: block;
}

@media (prefers-color-scheme: light) {
  .suggestion-thumb {
    border-color: var(--border);
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
  }
}

.suggestion-name {
  font-weight: 600;
  font-size: 0.95rem;
}

.suggestion-sku {
  color: #9ca3af;
  font-size: 0.875rem;
}

@media (prefers-color-scheme: light) {
  .suggestion-sku {
    color: var(--muted);
  }
}

.suggestion-price {
  font-weight: 700;
  font-size: 0.95rem;
  color: #10b981;
}

/* Botones */
.btn-primary {
  padding: 0.875rem 1.5rem;
  border-radius: 14px;
  background: linear-gradient(135deg, #C6923D 0%, #DFC05E 100%);
  border: none;
  color: #ffffff;
  font-weight: 600;
  font-size: 0.95rem;
  cursor: pointer;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  box-shadow: 0 4px 16px rgba(198, 146, 61, 0.3);
  position: relative;
  overflow: hidden;
}

.btn-primary::before {
  content: '';
  position: absolute;
  top: 0;
  left: -100%;
  width: 100%;
  height: 100%;
  background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.2), transparent);
  transition: left 0.5s;
}

.btn-primary:hover {
  transform: translateY(-2px);
  box-shadow: 0 8px 24px rgba(198, 146, 61, 0.4);
}

.btn-primary:hover::before {
  left: 100%;
}

.btn-primary:active {
  transform: translateY(0);
}

.btn-primary:disabled {
  opacity: 0.5;
  cursor: not-allowed;
  transform: none !important;
}

.btn-secondary {
  padding: 0.875rem 1.5rem;
  border-radius: 14px;
  background: var(--surface);
  border: 2px solid var(--border);
  color: var(--text);
  font-weight: 600;
  font-size: 0.95rem;
  cursor: pointer;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
}

.btn-secondary:hover {
  background: rgba(223, 192, 94, 0.08);
  border-color: #667eea;
  transform: translateY(-2px);
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.08);
}

/* Card Container */
.card {
  background: var(--surface);
  color: var(--text);
  border-radius: 20px;
  border: 1px solid var(--border);
  overflow: hidden;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.04);
  backdrop-filter: blur(10px);
  position: relative;
  z-index: 1;
}

@media (prefers-color-scheme: dark) {
  .card {
    background: rgba(255, 255, 255, 0.04);
    border-color: rgba(255, 255, 255, 0.08);
  }
}

/* Table */
.table-scroll {
  width: 100%;
  overflow-x: auto;
  position: relative;
}

.list {
  width: 100%;
  border-collapse: separate;
  border-spacing: 0;
}

.list thead tr {
  border-left: 4px solid transparent;
}

.list thead th {
  text-align: left;
  font-weight: 700;
  font-size: 0.8rem;
  letter-spacing: 0.5px;
  text-transform: uppercase;
  padding: 1rem 1.25rem;
  color: var(--muted);
  background: var(--surface);
  border-bottom: 2px solid var(--border);
  position: sticky;
  top: 0;
  z-index: 5;
  backdrop-filter: blur(10px);
}

.list tbody td {
  padding: 1rem 1.25rem;
  border-bottom: 1px solid var(--border);
  vertical-align: middle;
}

.list tbody tr {
  transition: all 0.2s ease;
  cursor: pointer;
  position: relative;
  border-left: 4px solid transparent;
}

.list tbody tr:hover {
  background: rgba(198, 146, 61, 0.04);
  border-left-color: #C6923D;
}

.list tbody tr.selected {
  background: rgba(198, 146, 61, 0.08);
  border-left-color: #C6923D;
}
/* Table Cells */
.thumb-col {
  width: 80px;
}

.thumb {
  width: 56px;
  height: 56px;
  object-fit: cover;
  object-position: center;
  border-radius: 12px;
  border: 2px solid var(--border);
  background: #fff;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
  transition: transform 0.2s ease;
  display: block;
}

.thumb:hover {
  transform: scale(1.05);
  z-index: 10;
}

.thumb.placeholder {
  display: grid;
  place-items: center;
  width: 56px;
  height: 56px;
  border-radius: 12px;
  border: 2px dashed var(--border);
  color: var(--muted);
  background: rgba(223, 192, 94, 0.04);
  font-size: 1.5rem;
}

.name {
  font-weight: 600;
  font-size: 0.95rem;
  color: var(--text);
}

.company {
  color: var(--muted);
  font-size: 0.9rem;
}

.qty,
.reorder {
  text-align: center;
  font-weight: 600;
  font-variant-numeric: tabular-nums;
}

.price {
  text-align: right;
  font-weight: 700;
  font-size: 1rem;
  color: #10b981;
  font-variant-numeric: tabular-nums;
}

.empty {
  text-align: center;
  color: var(--muted);
  padding: 3rem 1rem;
  font-size: 0.95rem;
}

/* Action Buttons */
.actions-col {
  text-align: right;
}

.row-actions {
  display: inline-flex;
  gap: 0.5rem;
  justify-content: flex-end;
}

.act-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  padding: 0;
  border-radius: 10px;
  border: none;
  background: rgba(223, 192, 94, 0.08);
  color: var(--accent);
  font-size: 0.85rem;
  cursor: pointer;
  transition: all 0.2s ease;
}

.act-btn .material-symbols-rounded {
  font-size: 20px;
}

.act-btn:hover {
  background: rgba(223, 192, 94, 0.15);
  transform: scale(1.08);
}

.act-btn.warn {
  background: rgba(239, 68, 68, 0.08);
  color: #ef4444;
}

.act-btn.warn:hover {
  background: rgba(239, 68, 68, 0.15);
}

.act-btn:disabled {
  opacity: 0.4;
  cursor: not-allowed;
  transform: none !important;
}

@media (prefers-color-scheme: dark) {
  .act-btn {
    background: rgba(198, 146, 61, 0.12);
  }
  
  .act-btn.warn {
    background: rgba(239, 68, 68, 0.12);
  }
}

/* Panel de Detalles */
.detail {
  padding: 1.5rem;
  display: grid;
  gap: 1rem;
}

.detail-title {
  font-size: 1.25rem;
  font-weight: 700;
  margin-bottom: 0.5rem;
  color: var(--text);
}

.detail-image {
  border: 2px solid var(--border);
  border-radius: 16px;
  overflow: hidden;
  background: rgba(223, 192, 94, 0.03);
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.06);
}

.detail-image img {
  width: 100%;
  max-height: 280px;
  object-fit: cover;
  display: block;
  transition: transform 0.3s ease;
}

.detail-image:hover img {
  transform: scale(1.05);
}

.thumbs {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 0.625rem;
  margin: 0.5rem 0;
}

.thumb-mini {
  width: 100%;
  height: 70px;
  object-fit: cover;
  border-radius: 12px;
  border: 2px solid var(--border);
  background: #fff;
  cursor: pointer;
  transition: all 0.2s ease;
}

.thumb-mini:hover {
  border-color: #C6923D;
  transform: scale(1.05);
}

.chips {
  display: flex;
  gap: 0.625rem;
  flex-wrap: wrap;
}

.chip {
  display: inline-flex;
  align-items: center;
  gap: 0.375rem;
  padding: 0.5rem 0.875rem;
  border-radius: 12px;
  background: rgba(198, 146, 61, 0.1);
  border: 2px solid rgba(198, 146, 61, 0.2);
  color: var(--text);
  font-size: 0.875rem;
  font-weight: 600;
  transition: all 0.2s ease;
}

.chip:hover {
  background: rgba(198, 146, 61, 0.15);
  border-color: rgba(198, 146, 61, 0.3);
  transform: translateY(-1px);
}

.chip.success {
  background: rgba(16, 185, 129, 0.1);
  border-color: rgba(16, 185, 129, 0.3);
  color: #10b981;
}

.chip.success:hover {
  background: rgba(16, 185, 129, 0.15);
}

.chip.price {
  background: linear-gradient(135deg, #fbbf24 0%, #f59e0b 100%);
  border-color: #f59e0b;
  color: #ffffff;
  font-weight: 700;
}

.chip.price:hover {
  box-shadow: 0 4px 12px rgba(251, 191, 36, 0.3);
}
/* Secciones del panel de detalles */
.section {
  margin-top: 1.25rem;
  padding-top: 1.25rem;
  border-top: 1px solid var(--border);
}

.section:first-of-type {
  margin-top: 0;
  padding-top: 0;
  border-top: none;
}

.section-head {
  font-weight: 700;
  font-size: 1rem;
  color: var(--text);
  margin-bottom: 0.875rem;
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.small {
  font-size: 0.85em;
  font-weight: 500;
}

.chip-row {
  display: flex;
  flex-wrap: wrap;
  gap: 0.625rem;
}

.chip-btn {
  padding: 0.5rem 1rem;
  border-radius: 10px;
  border: 2px solid var(--border);
  background: var(--surface);
  color: var(--text);
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
}

.chip-btn:hover {
  background: rgba(198, 146, 61, 0.08);
  border-color: #C6923D;
  transform: translateY(-1px);
}

.desc-list {
  padding-left: 1.25rem;
  color: var(--text);
  line-height: 1.8;
}

.desc-list li {
  margin: 0.5rem 0;
  font-size: 0.95rem;
}

.shipping-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 0.75rem;
}

.ship-item {
  display: flex;
  align-items: center;
  gap: 0.625rem;
  padding: 0.875rem;
  border: 2px solid var(--border);
  border-radius: 14px;
  background: var(--surface);
  transition: all 0.2s ease;
}

.ship-item:hover {
  background: rgba(198, 146, 61, 0.04);
  border-color: #C6923D;
  transform: translateY(-2px);
}

.ship-item .material-symbols-rounded {
  color: #C6923D;
  font-size: 24px;
}

.reviews {
  display: grid;
  grid-template-columns: 1fr;
  gap: 0.875rem;
}

.review-card {
  border: 2px solid var(--border);
  border-radius: 14px;
  padding: 1rem;
  background: var(--surface);
  transition: all 0.2s ease;
}

.review-card:hover {
  background: rgba(198, 146, 61, 0.04);
  border-color: #C6923D;
  transform: translateY(-2px);
}

.review-head {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  justify-content: space-between;
  margin-bottom: 0.625rem;
}

.review-head b {
  font-weight: 600;
  font-size: 0.95rem;
}

.stars {
  color: #fbbf24;
  font-size: 1rem;
}

.detail-grid {
  margin-top: 1.25rem;
  display: grid;
  gap: 0.5rem;
  padding: 1rem;
  background: rgba(198, 146, 61, 0.04);
  border-radius: 14px;
  border: 2px solid var(--border);
}

.detail-row {
  display: flex;
  justify-content: space-between;
  padding: 0.75rem 0;
  border-bottom: 1px solid var(--border);
}

.detail-row:last-child {
  border-bottom: none;
  padding-bottom: 0;
}

.detail-row span {
  color: var(--muted);
  font-size: 0.9rem;
  font-weight: 500;
}

.detail-row b {
  color: var(--text);
  font-size: 0.95rem;
  font-weight: 600;
}

.muted {
  color: var(--muted);
  line-height: 1.6;
}

/* Mejoras de contraste en modo oscuro */
@media (prefers-color-scheme: dark) {
  .search {
    background: rgba(198, 146, 61, 0.08);
    border-color: rgba(198, 146, 61, 0.15);
    color: #e5e7eb;
  }
  
  .search:hover {
    background: rgba(198, 146, 61, 0.12);
  }
  
  .search:focus {
    background: rgba(255, 255, 255, 0.06);
    border-color: #C6923D;
  }
  
  .list thead th {
    background: rgba(255, 255, 255, 0.04);
    color: var(--muted);
  }
  
  .chip-btn,
  .ship-item,
  .review-card {
    background: rgba(255, 255, 255, 0.04);
    border-color: rgba(255, 255, 255, 0.08);
    color: #e5e7eb;
  }
  
  .chip.price {
    background: linear-gradient(135deg, #fbbf24 0%, #f59e0b 100%);
    border-color: #f59e0b;
    color: #ffffff;
  }
  
  .detail-grid {
    background: rgba(198, 146, 61, 0.08);
  }
}

/* Badges y Estados */
.badge {
  display: inline-flex;
  align-items: center;
  gap: 0.25rem;
  padding: 0.35rem 0.75rem;
  border-radius: 8px;
  font-size: 0.75rem;
  font-weight: 700;
  letter-spacing: 0.3px;
  text-transform: uppercase;
  margin-left: 0.5rem;
}

.badge.warn {
  background: linear-gradient(135deg, #f59e0b 0%, #d97706 100%);
  color: #ffffff;
  box-shadow: 0 2px 8px rgba(245, 158, 11, 0.3);
  animation: pulse 2s ease-in-out infinite;
}

@keyframes pulse {
  0%, 100% {
    opacity: 1;
  }
  50% {
    opacity: 0.85;
  }
}

.error {
  color: #ef4444;
  background: rgba(239, 68, 68, 0.1);
  border: 1px solid rgba(239, 68, 68, 0.2);
  border-radius: 12px;
  padding: 1rem 1.25rem;
  font-weight: 500;
}

/* Barcode */
.barcode-mini {
  width: 100%;
  max-width: 140px;
  height: 42px;
  display: block;
  margin: 0 auto;
}

/* Paginación */
.pagination {
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 0.75rem;
  padding: 1rem 0;
}

.pager-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  padding: 0;
  border-radius: 12px;
  border: 2px solid var(--border);
  background: var(--surface);
  color: var(--text);
  cursor: pointer;
  transition: all 0.2s ease;
}

.pager-btn:hover:not(:disabled) {
  background: rgba(198, 146, 61, 0.08);
  border-color: #C6923D;
  transform: scale(1.05);
}

.pager-btn:disabled {
  opacity: 0.3;
  cursor: not-allowed;
}

.pager-info {
  font-weight: 600;
  font-size: 0.95rem;
  color: var(--text);
  padding: 0 0.5rem;
  min-width: 120px;
  text-align: center;
}
/* Responsivo */
@media (max-width: 1400px) {
  .layout {
    grid-template-columns: 1fr;
  }
  
  .panel-right {
    order: -1;
  }
}

@media (max-width: 1024px) {
  .layout {
    padding: 1rem;
    gap: 1rem;
  }
  
  .page-title-main {
    font-size: 1.75rem;
  }
  
  .stats-grid {
    grid-template-columns: repeat(2, 1fr);
    gap: 1rem;
  }
  
  .stat-card {
    padding: 1.5rem 1.25rem;
  }
  
  .stat-value {
    font-size: 1.75rem;
  }
  
  .toolbar {
    flex-direction: column;
    align-items: stretch;
    gap: 1rem;
  }
  
  .toolbar-search {
    max-width: 100%;
  }
  
  .toolbar-actions {
    width: 100%;
    justify-content: stretch;
  }
  
  .btn-primary,
  .btn-secondary {
    flex: 1;
  }
  
  .shipping-grid {
    grid-template-columns: 1fr;
  }
}

@media (max-width: 768px) {
  .layout {
    padding: 0.75rem;
  }
  
  .page-title-main {
    font-size: 1.5rem;
  }
  
  .page-subtitle {
    font-size: 0.9rem;
  }
  
  .stats-grid {
    grid-template-columns: 1fr;
  }
  
  .stat-card {
    padding: 1.25rem 1rem;
  }
  
  .stat-icon {
    width: 54px;
    height: 54px;
  }
  
  .stat-icon .material-symbols-rounded {
    font-size: 28px;
  }
  
  .stat-value {
    font-size: 1.5rem;
  }
  
  .toolbar {
    padding: 0.875rem;
  }
  
  .search {
    padding: 0.75rem 2.5rem 0.75rem 3rem;
    font-size: 0.9rem;
  }
  
  .btn-primary,
  .btn-secondary {
    padding: 0.75rem 1.25rem;
    font-size: 0.9rem;
  }
  
  .list thead th,
  .list tbody td {
    padding: 0.875rem 0.75rem;
    font-size: 0.875rem;
  }
  
  .thumb,
  .thumb.placeholder {
    width: 42px;
    height: 42px;
  }
  
  .act-btn {
    width: 32px;
    height: 32px;
  }
  
  .act-btn .material-symbols-rounded {
    font-size: 18px;
  }
  
  .detail {
    padding: 1rem;
  }
  
  .detail-title {
    font-size: 1.1rem;
  }
  
  .thumbs {
    grid-template-columns: repeat(2, 1fr);
  }
  
  .reviews {
    grid-template-columns: 1fr;
  }
}

@media (max-width: 480px) {
  .toolbar-actions {
    flex-direction: column;
  }
  
  .btn-primary span:not(.material-symbols-rounded),
  .btn-secondary span:not(.material-symbols-rounded) {
    display: none;
  }
  
  .btn-primary,
  .btn-secondary {
    width: 100%;
    justify-content: center;
  }
  
  .table-scroll {
    overflow-x: scroll;
    -webkit-overflow-scrolling: touch;
  }
  
  .list {
    min-width: 600px;
  }
  
  .stat-label {
    font-size: 0.7rem;
  }
}
</style>
